import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2, Search, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { searchBelgianCompany } from "@/utils/company-search";

interface CompanyLogoEditProps {
  logoUrl: string | null;
  companyName: string;
  vatNumber?: string;
  onLogoChange: (logoUrl: string | undefined) => void;
}

export const CompanyLogoEdit = ({ 
  logoUrl, 
  companyName, 
  vatNumber,
  onLogoChange 
}: CompanyLogoEditProps) => {
  const { t } = useTranslation();
  const [isSearching, setIsSearching] = useState(false);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [logoResults, setLogoResults] = useState<{ brandId: string; name: string; icon: string; }[]>([]);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null);

  const searchAndShowLogos = async () => {
    if (!companyName && !vatNumber) {
      toast({
        title: "Missing Information",
        description: "A company name or VAT number is required to search for a logo.",
        variant: "destructive",
      });
      return;
    }

    setIsSearching(true);
    try {
      // Search using company name or VAT number
      const searchTerm = companyName || vatNumber;
      const result = await searchBelgianCompany(searchTerm);
      
      if (!result || result.logos.length === 0) {
        toast({
          title: "Logo Not Found",
          description: "No logo was found for this company.",
        });
        return;
      }

      // Open dialog with logo options
      setLogoResults(result.logos);
      setIsLogoDialogOpen(true);
    } catch (error) {
      console.error("Error searching for company logo:", error);
      toast({
        title: "Search Failed",
        description: "Failed to search for company logo.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const confirmLogoSelection = () => {
    if (selectedLogoUrl) {
      onLogoChange(selectedLogoUrl);
      setIsLogoDialogOpen(false);
      setSelectedLogoUrl(null);
      toast({
        title: "Logo Selected",
        description: "Company logo has been updated successfully.",
      });
    }
  };

  const handleClearLogo = () => {
    onLogoChange(undefined);
    toast({
      title: "Logo Removed",
      description: "Company logo has been removed.",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative h-20 w-20 bg-digger-light-gray rounded-sm flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={`${companyName} logo`} 
                className="h-16 w-16 object-contain" 
                onError={(e) => {
                  // If the image fails to load, show a placeholder
                  (e.target as HTMLImageElement).src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWJ1aWxkaW5nLTIiPjxyZWN0IHdpZHRoPSIxNiIgaGVpZ2h0PSIyNCIgeD0iNCIgeT0iMiIgcng9IjIiIHJ5PSIyIi8+PHBhdGggZD0iTTkgMjJ2LTRoNnY0Ii8+PHBhdGggZD0iTTggNmguMDEiLz48cGF0aCBkPSJNMTYgNmguMDEiLz48cGF0aCBkPSJNMTIgNmguMDEiLz48cGF0aCBkPSJNOCAxMGguMDEiLz48cGF0aCBkPSJNMTYgMTBoLjAxIi8+PHBhdGggZD0iTTEyIDEwaC4wMSIvPjxwYXRoIGQ9Ik04IDE0aC4wMSIvPjxwYXRoIGQ9Ik0xNiAxNGguMDEiLz48cGF0aCBkPSJNMTIgMTRoLjAxIi8+PC9zdmc+";
                }}
              />
            ) : (
              <Building2 className="h-10 w-10 text-gray-500 dark:text-gray-400" />
            )}
            <Button 
              variant="outline" 
              size="icon" 
              className="absolute -top-2 -right-2 h-8 w-8 rounded-sm bg-white dark:bg-gray-800"
              onClick={searchAndShowLogos}
              disabled={isSearching}
              title={t("companies.search.searchForLogo", "Search for company logo")}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <div>
            <p className="text-sm font-medium">{t("companies.createModal.logoLabel", "Company Logo")}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {logoUrl 
                ? t("companies.createModal.logoSearchHelp", "Click the search icon to find logos automatically")
                : t("companies.createModal.noLogo", "No Logo")
              }
            </p>
          </div>
          {logoUrl && (
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={handleClearLogo}
              className="h-8"
              title={t("companies.createModal.removeLogo", "Remove Logo")}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>

      {/* Logo Search Dialog */}
      <Dialog open={isLogoDialogOpen} onOpenChange={setIsLogoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("companies.search.selectLogo", "Select Company Logo")}</DialogTitle>
            <DialogDescription>
              {t("companies.search.selectLogoDescription", "Choose a logo for this company")}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-60 my-4">
            <RadioGroup
              value={selectedLogoUrl || ""}
              onValueChange={setSelectedLogoUrl}
              className="grid grid-cols-2 sm:grid-cols-3 gap-3"
            >
              {logoResults.map((logo) => (
                <Label
                  key={logo.brandId}
                  htmlFor={`logo-${logo.brandId}`}
                  className={`relative flex flex-col items-center justify-center p-2 border rounded-sm cursor-pointer hover:border-digger-blue ${selectedLogoUrl === logo.icon ? 'border-digger-blue ring-2 ring-digger-blue' : ''}`}
                >
                  <RadioGroupItem value={logo.icon} id={`logo-${logo.brandId}`} className="sr-only" />
                  <img src={logo.icon} alt={`${logo.name} logo`} className="h-12 w-12 object-contain mb-1" />
                  <span className="text-xs text-center truncate w-full">{logo.name}</span>
                </Label>
              ))}
            </RadioGroup>
          </ScrollArea>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsLogoDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={confirmLogoSelection} disabled={!selectedLogoUrl}>
              {t("common.confirm", "Confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 